import React, { useEffect, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import { Cookie } from '@/tscapi/utils/cookie';

import { en, fr } from './i18n';
import { SigninForm } from './SigninForm';

declare let grecaptcha: any;

export const Signin = () => {
    const cookie: Cookie = new Cookie();

    const isCheckout = cookie.TargetUrl === '/pages/expresscheckout';
    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;

    useEffect(() => {
        const loadReCaptcha = () => {
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js?render=' + reCaptchaSiteKey;
            document.body.appendChild(script);
        };

        loadReCaptcha();
    }, [reCaptchaSiteKey]);

    const executeRecaptcha = useCallback(async (): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (typeof grecaptcha !== 'undefined') {
                grecaptcha
                    .execute(reCaptchaSiteKey, { action: 'signin' })
                    .then((token: string) => resolve(token))
                    .catch((error: any) => reject(error));
            } else {
                reject(new Error('reCAPTCHA not loaded'));
            }
        });
    }, [reCaptchaSiteKey]);

    const getCurrentLanguage = (): 'en' | 'fr' => {
        return 'en';
    };
    const currentLanguage = getCurrentLanguage();
    const t = currentLanguage === 'fr' ? fr : en;

    const title = isCheckout ? t.Checkout : t['Sign In'];

    return (
        <ErrorBoundary fallback={<ErrorFallback />}>
            <div className="signin__wrapper mx-auto w-full max-w-[598px] px-5 py-8">
                <SigninForm isCheckout={isCheckout} title={title} executeRecaptcha={executeRecaptcha} />
                <b>A TSC account lets you:</b>
                <ul className="my-[5px] list-none p-0 text-[13px]">
                    <li className="mb-2 flex items-start">
                        <CheckmarkSmallSvg className="mt-1 min-w-3.5" />
                        <span className="ml-2">Checkout faster</span>
                    </li>
                    <li className="mb-2 flex items-start">
                        <CheckmarkSmallSvg className="mt-1 min-w-3.5" />
                        <span className="ml-2">
                            Split your purchase price over interest free payments with{' '}
                            <a
                                href="/pages/customer-service/easy-pay"
                                className="underline hover:font-semibold hover:underline"
                            >
                                Easy Pay
                            </a>
                        </span>
                    </li>
                    <li className="flex items-start">
                        <CheckmarkSmallSvg className="mt-1 min-w-3.5" />
                        <span className="ml-2">
                            Enjoy personalized recommendations tailored to your shopping preferences
                        </span>
                    </li>
                </ul>
                <p className="mt-12 text-[13px]">
                    Shop with Confidence.{' '}
                    <span className="text-nowrap">
                        We value your{' '}
                        <a href="/go/aboutus/aboutusprivacy" className="underline hover:font-semibold hover:underline">
                            Privacy and Security
                        </a>
                    </span>
                </p>
            </div>
        </ErrorBoundary>
    );
};

const ErrorFallback = () => {
    return (
        <div className="summary" style={{ background: '#fff' }}>
            <div className="summary__loading">
                <div style={{ textAlign: 'center', padding: '15px 24px 0' }}>
                    Error loading the Signin.
                    <br /> Please reload the page.
                </div>
            </div>
        </div>
    );
};
